import { Link } from "react-router-dom";
import { Button, buttonClass } from "@a2z/ui";
import { keys } from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";

type RouteErrorFallbackProps = {
  /** Re-render the failed subtree (Sentry's `resetError`). */
  onRetry: () => void;
  /** Where the "go to dashboard" escape hatch points. */
  homeHref: string;
};

/**
 * In-pane fallback shown when a route's content throws. Fills the content
 * area rather than the viewport, so the surrounding shell (sidebar/topbar)
 * stays usable. Direction/locale are inherited from the `.seller-app`
 * wrapper, so no `dir` handling is needed here.
 */
export function RouteErrorFallback({ onRetry, homeHref }: RouteErrorFallbackProps) {
  const t = useTranslations();

  return (
    <div
      role="alert"
      className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-5 p-6 text-center"
    >
      <div className="max-w-md">
        <h1 className="text-base font-semibold text-[color:var(--text-primary)]">
          {t(keys.errors.crashTitle)}
        </h1>
        <p className="mt-2 text-sm text-[color:var(--text-muted)]">
          {t(keys.errors.recovery)}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Button type="button" variant="primary" size="md" onClick={onRetry}>
          {t(keys.common.retry)}
        </Button>
        <Link to={homeHref} className={buttonClass({ variant: "outline", size: "md" })}>
          {t(keys.errors.backToDashboard)}
        </Link>
      </div>
    </div>
  );
}
